import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Icon } from '@iconify/react';
import { alpha, Box, Button, FormHelperText, IconButton, Typography, useTheme } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useResponsive } from '@ui/style/useResponsive';
import CustomDialog from '@ui/components/common/Dialog';
import BDrawer from '@ui/components/common/BDrawer';
import ControlledFilledTextField from '@ui/components/common/TextField/ControlledFilledTextField';
import HugeIconsUpload from '@ui/assets/custom-icons/upload-icon';
import { Iconly } from 'react-iconly';
const AddInformationDialog = ({ open, onClose, onSubmit, onEdit, initialData, isEdit }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { isMobile } = useResponsive();
    const { control, handleSubmit, reset, setValue, watch, formState, setError, clearErrors } = useForm({
        defaultValues: {
            title: initialData?.title || '',
            description: initialData?.description || '',
            file: initialData?.file || null
        }
    });
    React.useEffect(() => {
        if (initialData) {
            // If editing, populate form
            reset({
                title: initialData.title,
                description: initialData.description,
                file: initialData.file || null
            });
        }
        else {
            // If adding new, reset to empty
            reset({ title: '', description: '', file: null });
        }
    }, [initialData, reset]);
    const file = watch('file');
    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const selectedFile = e.target.files[0];
            const ext = selectedFile.name.match(/\.([^.]+)$/i)?.[1]?.toLowerCase() ?? '';
            const isAllowedImageExt = ext === 'png' || ext === 'jpg' || ext === 'jpeg';
            const isAllowedImageMime = selectedFile.type === 'image/png' ||
                selectedFile.type === 'image/jpeg' ||
                selectedFile.type === 'image/jpg';
            const isAllowedImage = isAllowedImageMime || isAllowedImageExt;
            const isAllowedVideoExt = ext === 'mp4' || ext === 'mkv';
            const isAllowedVideoMime = selectedFile.type === 'video/mp4' ||
                selectedFile.type === 'video/x-matroska' ||
                selectedFile.type === 'video/mkv';
            const isAllowedVideo = isAllowedVideoMime || isAllowedVideoExt;
            const isAllowedPdfExt = ext === 'pdf';
            const isAllowedPdfMime = selectedFile.type === 'application/pdf';
            const isAllowedPdf = isAllowedPdfMime || isAllowedPdfExt;
            if (isAllowedPdf) {
                const maxPdfSize = 10 * 1024 * 1024; // 10MB
                if (selectedFile.size > maxPdfSize) {
                    setError('file', {
                        type: 'validate',
                        message: t('fileUpload.pdfSizeError') || 'PDF file size must not exceed 10MB.'
                    });
                    e.target.value = '';
                    return;
                }
                setValue('file', selectedFile);
                clearErrors('file');
                return;
            }
            if (isAllowedImage || isAllowedVideo) {
                const maxSize = 10 * 1024 * 1024; // 10MB
                if (selectedFile.size > maxSize) {
                    setError('file', {
                        type: 'validate',
                        message: t('fileUpload.fileSizeError', { count: 10 }) ||
                            'File size must not exceed 10MB.'
                    });
                    e.target.value = '';
                    return;
                }
                setValue('file', selectedFile);
                clearErrors('file');
                return;
            }
            setError('file', {
                type: 'validate',
                message: t('fileUpload.allowedFileTypesError') ||
                    'Only PNG, JPG, or JPEG images, MP4/MKV videos, or PDF files (max 10MB) are allowed.'
            });
            e.target.value = '';
        }
    };
    const handleDialogClose = () => {
        reset();
        onClose();
    };
    const handleSubmitInternal = (data) => {
        // When adding new, file is required
        if (!onEdit && !data.file) {
            setError('file', {
                type: 'required',
                message: t('validations.required', {
                    field: t('events.createNewEvent.attachment')
                })
            });
            return;
        }
        if (onEdit) {
            const hasExistingFile = typeof initialData?.file === 'string' && initialData.file.length > 0;
            const hasCurrentFile = !!data.file;
            if (!hasCurrentFile && !hasExistingFile) {
                setError('file', {
                    type: 'required',
                    message: t('validations.required', {
                        field: t('events.createNewEvent.attachment')
                    })
                });
                return;
            }
        }
        const fileToPass = data.file instanceof File ? data.file : null;
        if (onEdit && initialData) {
            onEdit({ title: data.title, description: data.description, file: fileToPass }); // call onEdit if editing
        }
        else {
            onSubmit({ title: data.title, description: data.description, file: fileToPass }); // else call onSubmit
        }
        reset(); // reset form after successful submit
    };
    const actionButtons = (_jsxs(Box, { sx: { display: 'flex', gap: 1.5, width: '100%' }, children: [_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', size: 'large', onClick: handleDialogClose, children: t('actions.cancel') || 'Cancel' }), _jsx(Button, { fullWidth: true, variant: 'contained', color: 'inherit', size: 'large', onClick: handleSubmit(handleSubmitInternal), children: isEdit ? t('actions.update') || 'Update' : t('actions.add') || 'Add' })] }));
    const content = (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 1, children: [_jsx(ControlledFilledTextField, { name: 'title', control: control, label: t('events.createNewEvent.title'), rules: {
                    required: t('validations.required', {
                        field: t('events.createNewEvent.title')
                    })
                }, errorMessage: formState.errors?.title?.message, bg: 'grey8', fullWidth: true }), _jsx(ControlledFilledTextField, { name: 'description', control: control, label: t('events.createNewEvent.description'), rules: {
                    required: t('validations.required', {
                        field: t('events.createNewEvent.description')
                    })
                }, errorMessage: formState.errors?.description?.message, bg: 'grey8', fullWidth: true, multiline: true, rows: 4 }), (file || (initialData && typeof initialData.file === 'string')) && (_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 2, sx: {
                    border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
                    borderRadius: 2,
                    p: 1.5,
                    background: alpha(theme.palette.info.main, 0.04)
                }, children: [(() => {
                        // Priority: current file > initialData file
                        const displayFile = file || (initialData?.file ?? null);
                        if (displayFile instanceof File) {
                            // New file uploaded
                            if (displayFile.type.startsWith('video/')) {
                                return (_jsx(Box, { sx: {
                                        width: 48,
                                        height: 48,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: 1,
                                        bgcolor: alpha(theme.palette.info.main, 0.1)
                                    }, children: _jsx(Iconly, { name: 'Video', set: 'light', size: 24 }) }));
                            }
                            else if (displayFile.type === 'application/pdf') {
                                return (_jsx(Box, { sx: {
                                        width: 48,
                                        height: 48,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: 1,
                                        bgcolor: alpha(theme.palette.error.main, 0.1)
                                    }, children: _jsx(Icon, { icon: 'mdi:file-pdf-box', width: 32, height: 32, color: theme.palette.error.main }) }));
                            }
                            else {
                                return (_jsx(Box, { component: 'img', src: URL.createObjectURL(displayFile), alt: displayFile.name, sx: { width: 48, height: 48, objectFit: 'cover', borderRadius: 1 }, onError: (e) => {
                                        const target = e.target;
                                        target.style.display = 'none';
                                    } }));
                            }
                        }
                        else if (typeof displayFile === 'string') {
                            // Existing URL from backend — blob-proxy URLs don't have extension in the URL,
                            // so also check documentName for type detection
                            const docName = initialData?.documentName ?? '';
                            const isVideo = displayFile.match(/\.(mp4|webm|ogg|mov|avi|mkv)$/i) ||
                                docName.match(/\.(mp4|webm|ogg|mov|avi|mkv)$/i);
                            const isPdf = displayFile.match(/\.pdf$/i) || docName.match(/\.pdf$/i);
                            if (isVideo) {
                                return (_jsx(Box, { sx: {
                                        width: 48,
                                        height: 48,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: 1,
                                        bgcolor: alpha(theme.palette.info.main, 0.1)
                                    }, children: _jsx(Iconly, { name: 'Video', set: 'light', size: 24 }) }));
                            }
                            else if (isPdf) {
                                return (_jsx(Box, { sx: {
                                        width: 48,
                                        height: 48,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: 1,
                                        bgcolor: alpha(theme.palette.error.main, 0.1)
                                    }, children: _jsx(Icon, { icon: 'mdi:file-pdf-box', width: 32, height: 32, color: theme.palette.error.main }) }));
                            }
                            else {
                                return (_jsx(Box, { component: 'img', src: displayFile, alt: 'Document preview', sx: { width: 48, height: 48, objectFit: 'cover', borderRadius: 1 }, onError: (e) => {
                                        const target = e.target;
                                        target.style.display = 'none';
                                    } }));
                            }
                        }
                        else {
                            return _jsx(HugeIconsUpload, { color: theme.palette.info.main, size: 32 });
                        }
                    })(), _jsxs(Box, { flex: 1, minWidth: 0, children: [_jsx(Box, { fontWeight: 500, fontSize: 15, color: theme.palette.text.primary, sx: { wordBreak: 'break-all' }, children: (() => {
                                    const displayFile = file || (initialData?.file ?? null);
                                    if (displayFile instanceof File) {
                                        return displayFile.name;
                                    }
                                    else if (typeof displayFile === 'string') {
                                        // Prefer the stored document name over extracting from URL
                                        if (initialData?.documentName) {
                                            return initialData.documentName;
                                        }
                                        return 'Existing Document';
                                    }
                                    return 'No file';
                                })() }), file instanceof File && (_jsx(Box, { fontSize: 13, color: theme.palette.text.secondary, children: `${(file.size / 1024).toFixed(1)} KB` })), (!file ||
                                typeof file === 'string' ||
                                (initialData && typeof initialData.file === 'string')) && (_jsx(Box, { fontSize: 13, color: theme.palette.text.secondary, children: t('events.createNewEvent.existingDocument') || 'Click upload to replace' }))] }), _jsx(IconButton, { onClick: () => {
                            setValue('file', null);
                            clearErrors('file');
                        }, style: { border: 'none' }, children: _jsx(Iconly, { name: 'Delete', set: 'light', primaryColor: theme.palette.error.main, style: { cursor: 'pointer', width: 24, height: 24 } }) })] })), _jsx("input", { type: 'file', accept: 'image/png,image/jpeg,.png,.jpg,.jpeg,.mp4,.mkv,video/mp4,video/x-matroska,video/mkv,.pdf,application/pdf', style: { display: 'none' }, id: 'add-info-upload', onChange: handleFileChange }), _jsx("label", { htmlFor: 'add-info-upload', children: _jsx(Button, { variant: 'outlined', size: 'large', color: 'info', fullWidth: true, startIcon: _jsx(HugeIconsUpload, { color: theme.palette.info.main }), component: 'span', sx: {
                        border: `1px solid ${alpha(theme.palette.info.main, 0.48)}`,
                        color: theme.palette.info.main,
                        '&:hover': {
                            backgroundColor: 'transparent',
                            borderColor: alpha(theme.palette.info.main, 0.48),
                            color: theme.palette.info.main
                        }
                    }, children: initialData && typeof initialData.file === 'string'
                        ? t('actions.replaceFile') || 'Replace File'
                        : t('actions.uploadFile') }) }), _jsx(FormHelperText, { children: _jsxs(Box, { display: 'flex', gap: 0.5, children: [_jsx(Box, { children: _jsx(Icon, { icon: 'eva:info-fill', width: '18', height: '18', color: theme.palette.common.grey.dark }) }), _jsx(Box, { children: _jsx(Typography, { variant: 'caption', color: theme.palette.common.grey.dark, children: t('fileUpload.allowedFileTypesInfo') }) })] }) }), formState.errors?.file?.message && (_jsx(Box, { mt: 0.5, fontSize: 12, color: theme.palette.error.main, children: formState.errors.file.message }))] }));
    return isMobile ? (_jsx(BDrawer, { anchor: 'bottom', open: open, onClose: handleDialogClose, title: isEdit
            ? t('events.createNewEvent.editExtraInformation')
            : t('events.createNewEvent.addExtraInformation'), dialogActions: actionButtons, showCloseButton: true, children: content })) : (_jsx(CustomDialog, { open: open, onClose: handleDialogClose, titleIcon: '/assets/images/editTask.png', titleWithIcon: isEdit
            ? t('events.createNewEvent.editExtraInformation')
            : t('events.createNewEvent.addExtraInformation'), actions: actionButtons, children: _jsx(Box, { p: 1.5, children: content }) }));
};
export default AddInformationDialog;
